import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { searchAPI } from '../../services/api';
import { DownloadCloud, LogOut, Shield, ArrowLeft } from 'lucide-react';
import createAndDownloadBackup from '../../utils/backupDownload';
import toast from 'react-hot-toast';
import ApplicationTitleBar from './ApplicationTitleBar';
import MainMenu from './MainMenu';
import ApplicationToolbar from './ApplicationToolbar';

/**
 * Desktop application shell. Replaces the old sidebar layout with a
 * Windows-style title bar, classic menu bar and dark navy toolbar.
 * Global search lives in the toolbar; page content renders in <Outlet/>.
 */
export default function Layout() {
  const { user, company, logout, subscription, isPendingSubscription, isTrialing, trialDaysLeft, impersonating, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);

  // Auto-logout after 24 hours of expired subscription
  useEffect(() => {
    if (!subscription || subscription.status !== 'expired') return;
    const expiryDate = subscription.current_period_end || subscription.trial_ends_at;
    if (!expiryDate) {
      logout();
      navigate('/login', { replace: true });
      return;
    }
    const hoursSinceExpiry = (new Date() - new Date(expiryDate)) / 3600000;
    if (hoursSinceExpiry > 24) {
      toast.error('Your subscription has expired. Please renew to continue.');
      logout();
      navigate('/login', { replace: true });
    }
  }, [subscription, logout, navigate]);

  // Calculate expired overlay info
  const isExpired = subscription?.status === 'expired';
  const expiredOverlay = (() => {
    if (!isExpired) return null;
    const expiryDate = subscription.current_period_end || subscription.trial_ends_at;
    if (!expiryDate) return { hoursLeft: 0, daysLeft: 0 };
    const hoursSinceExpiry = (new Date() - new Date(expiryDate)) / 3600000;
    const hoursLeft = Math.max(0, Math.ceil(24 - hoursSinceExpiry));
    const daysLeft = Math.ceil(hoursLeft / 24);
    return { hoursLeft, daysLeft, expiryDate };
  })();

  // Restore persisted RTL/LTR direction
  useEffect(() => {
    const dir = localStorage.getItem('ledgerpro_dir');
    if (dir === 'rtl') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'fa';
    }
  }, []);

  useEffect(() => {
    setShowResults(false);
  }, [location.pathname]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.length >= 2) {
        try {
          const results = await searchAPI.search(searchQuery);
          setSearchResults(results || []);
          setShowResults(true);
        } catch (err) { console.error(err); }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchSelect = useCallback((result) => {
    setShowResults(false);
    setSearchQuery('');
    const routes = {
      customer: `/customers/${result.id}`,
      vendor: `/vendors/${result.id}`,
      invoice: `/invoices/${result.id}`,
      bill: `/bills/${result.id}`,
      account: '/accounts',
      product: `/products/${result.id}`,
      journal: `/journal/${result.id}`,
      payment: '/payments',
      expense: '/expenses',
      estimate: '/estimates',
      sales_order: '/sales-orders',
      purchase_order: '/purchase-orders',
      employee: '/employees',
    };
    navigate(routes[result.module] || '/dashboard');
  }, [navigate]);

  // Logout first asks whether to create + download a backup.
  const handleLogout = () => setShowLogout(true);

  const doLogout = () => {
    logout();
    navigate('/login');
  };

  const doBackupAndLogout = async () => {
    setLogoutBusy(true);
    try {
      await createAndDownloadBackup('Manual');
      toast.success('Backup created and downloaded');
      setShowLogout(false);
      doLogout();
    } catch (err) {
      toast.error('Backup failed: ' + err.message);
      setLogoutBusy(false);
    }
  };

  const canBackup = user?.permissions?.includes('backup:create');

  return (
    <div className="desk-app">
      <ApplicationTitleBar companyName={company?.name || user?.company_name || 'My Company'} username={user?.username} />

      {/* Impersonation banner — superadmin viewing a company */}
      {impersonating && (
        <div style={{
          background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
          color: '#fff',
          padding: '8px 16px',
          fontSize: 12,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={14} />
            <span>
              Super Admin viewing as: <strong>{impersonating.companyName}</strong>
              {impersonating.companyEmail && <span style={{ opacity: 0.8 }}> ({impersonating.companyEmail})</span>}
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              stopImpersonation();
              navigate('/super-admin');
              toast.success('Returned to admin portal');
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              color: '#fff',
              borderRadius: 4,
              padding: '4px 12px',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            <ArrowLeft size={12} /> Exit to Admin
          </button>
        </div>
      )}

      <MainMenu onLogout={handleLogout} />
      <ApplicationToolbar
        user={user}
        subscription={subscription}
        isPendingSubscription={isPendingSubscription}
        isTrialing={isTrialing}
        trialDaysLeft={trialDaysLeft}
        onLogout={handleLogout}
        activePath={location.pathname}
        searchProps={{ query: searchQuery, setQuery: setSearchQuery, results: searchResults, showResults, onSelect: handleSearchSelect }}
      />
      {/* Subscription warning banner — shows for expiring/urgent subscriptions */}
      {(() => {
        if (!subscription) return null;
        const status = subscription.status;
        const planName = subscription.plan_name || '';

        // Calculate days remaining
        let daysLeft = 0;
        let isUrgent = false;
        let bannerType = null; // 'urgent' | 'trial' | 'pending' | 'expired'

        if (status === 'active') {
          const endDate = subscription.current_period_end;
          if (endDate) {
            daysLeft = Math.max(0, Math.ceil((new Date(endDate) - new Date()) / 86400000));
          }
          if (daysLeft <= 7) {
            isUrgent = true;
            bannerType = 'urgent';
          }
        } else if (status === 'trialing') {
          daysLeft = trialDaysLeft ? trialDaysLeft() : 0;
          if (daysLeft <= 7) {
            isUrgent = true;
            bannerType = 'trial';
          } else {
            bannerType = 'trial'; // still show trial info
          }
        } else if (status === 'pending') {
          bannerType = 'pending';
        } else if (status === 'expired') {
          bannerType = 'expired';
          isUrgent = true;
          // Calculate hours remaining before auto-logout
          const expiryDate = subscription.current_period_end || subscription.trial_ends_at;
          if (expiryDate) {
            const hoursSinceExpiry = (new Date() - new Date(expiryDate)) / 3600000;
            daysLeft = Math.max(0, Math.ceil(24 - hoursSinceExpiry));
          }
        }

        if (!bannerType) return null;

        const configs = {
          urgent: {
            bg: '#fef2f2', border: '#fecaca', color: '#991b1b',
            icon: '⚠️', label: 'Subscription Expiring Soon',
            msg: `Your ${planName} subscription expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Renew now to avoid service interruption.`,
          },
          trial: {
            bg: isUrgent ? '#fef2f2' : '#eff6ff',
            border: isUrgent ? '#fecaca' : '#bfdbfe',
            color: isUrgent ? '#991b1b' : '#1e40af',
            icon: isUrgent ? '⚠️' : '🎁',
            label: isUrgent ? 'Trial Expiring Soon' : 'Free Trial',
            msg: isUrgent
              ? `Your ${planName} trial ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Subscribe now to keep access.`
              : `You're on the ${planName} free trial. ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining.`,
          },
          pending: {
            bg: '#fffbeb', border: '#fde68a', color: '#92400e',
            icon: '⏳', label: 'Pending Confirmation',
            msg: `Your ${planName} subscription is awaiting LedgerPro Accounting Sales confirmation.`,
          },
          expired: {
            bg: '#fef2f2', border: '#fecaca', color: '#991b1b',
            icon: '🚫', label: 'Subscription Expired',
            msg: daysLeft > 0
              ? `Your ${planName} subscription has expired. You will be logged out in ${daysLeft} hour${daysLeft !== 1 ? 's' : ''}. Renew now to keep access.`
              : `Your ${planName} subscription has expired. Your session has ended. Renew to regain access.`,
          },
        };

        const cfg = configs[bannerType];

        return (
          <div style={{
            background: cfg.bg,
            borderBottom: `1px solid ${cfg.border}`,
            padding: '8px 16px',
            fontSize: 12,
            color: cfg.color,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>{cfg.icon}</span>
            <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{cfg.label}</span>
            <span style={{ flex: 1 }}>{cfg.msg}</span>
            <button
              type="button"
              onClick={() => navigate('/subscription')}
              style={{
                background: cfg.color,
                color: '#fff',
                border: 'none',
                borderRadius: 4,
                padding: '5px 14px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                opacity: 1,
              }}
            >
              {bannerType === 'expired' ? 'Renew Now' : bannerType === 'pending' ? 'View Details' : 'Renew Now'}
            </button>
          </div>
        );
      })()}
      <div className="page-content" style={{ flex: 1, padding: 12, overflowY: 'auto', position: 'relative' }}>
        <Outlet />

        {/* Full-screen expired subscription overlay */}
        {expiredOverlay && (
          <div style={{
            position: 'absolute',
            inset: 0,
            zIndex: 100,
            background: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
              maxWidth: 480,
              width: '100%',
              overflow: 'hidden',
            }}>
              {/* Header */}
              <div style={{
                background: 'linear-gradient(135deg, #dc2626, #991b1b)',
                padding: '28px 32px 24px',
                textAlign: 'center',
                color: '#fff',
              }}>
                <div style={{ fontSize: 48, marginBottom: 8 }}>🚫</div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Subscription Expired</h2>
                <p style={{ margin: '6px 0 0', fontSize: 13, opacity: 0.9 }}>
                  Your <strong>{subscription.plan_name || 'plan'}</strong> subscription has expired.
                </p>
              </div>

              {/* Body */}
              <div style={{ padding: '24px 32px', textAlign: 'center' }}>
                {expiredOverlay.hoursLeft > 0 ? (
                  <>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: 6,
                      padding: '8px 16px',
                      marginBottom: 16,
                    }}>
                      <span style={{ fontSize: 14 }}>⏰</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#991b1b' }}>
                        {expiredOverlay.hoursLeft} hour{expiredOverlay.hoursLeft !== 1 ? 's' : ''} remaining before auto-logout
                      </span>
                    </div>
                    <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                      You can still access your account during the grace period.
                      Renew your subscription now to avoid service interruption and keep full access to all features.
                    </p>
                  </>
                ) : (
                  <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                    Your grace period has ended. Renew your subscription to regain access to your account.
                  </p>
                )}

                {/* Plan details */}
                <div style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  padding: '12px 16px',
                  marginBottom: 20,
                  textAlign: 'left',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>Plan</span>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{subscription.plan_name || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>Billing Cycle</span>
                    <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{subscription.billing_cycle || 'Monthly'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>Expired On</span>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>
                      {new Date(expiredOverlay.expiryDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => { logout(); navigate('/login'); }}
                  >
                    Sign Out
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ padding: '8px 28px', fontSize: 13 }}
                    onClick={() => navigate('/subscription')}
                  >
                    💳 Renew Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showLogout && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <h3>Backup before logout?</h3>
              <button type="button" className="btn-ghost" disabled={logoutBusy} onClick={() => setShowLogout(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ margin: 0, fontSize: 14 }}>
                Do you want to create a backup and download it to your computer before logging out?
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" disabled={logoutBusy} onClick={() => setShowLogout(false)}>Cancel</button>
              <button type="button" className="btn btn-outline" disabled={logoutBusy} onClick={doLogout}>
                <LogOut size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} /> Logout Without Backup
              </button>
              {canBackup && (
                <button type="button" className="btn btn-primary" disabled={logoutBusy} onClick={doBackupAndLogout}>
                  <DownloadCloud size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                  {logoutBusy ? 'Creating backup...' : 'Create Backup & Download'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
